import { type ReactNode, useEffect } from 'react'

import { faGavel } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { parseISO, differenceInSeconds } from 'date-fns'
import { useFormikContext } from 'formik'

import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  ddLogger,
  interpUri,
  usePrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'
import type { OrderResponseDto } from '@b-stock/order-api-client'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import { useBidFormContext } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import type { BidFormFormikValues } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { AuctionState } from '@/buyer-portal/components/features/listing/actions/auction/const'
import type { AuctionDetailState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import { isContractListing } from '@/buyer-portal/components/features/listing/actions/auction/utils'
import getTypicalSellingPriceAnalyticsData from '@/buyer-portal/components/features/listing/helpers/getTypicalSellingPriceAnalyticsData'
import { useCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/hooks/useCostContextAnalyticsProperties'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import { useUserAccountId } from '@/buyer-portal/helpers/accountId'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import useCompatibleRouter from '@/buyer-portal/helpers/useCompatibleRouter'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useIsMobile } from '@/buyer-portal/hooks/layout'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { redirectToAuth } from '@/helpers/auth'
import { FeatureFlags } from '@/helpers/featureFlags'
import { getUserMinBidAmountForAuction } from '@/helpers/getUserMinBidAmount'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { QuickBidButtons } from '../../Bid/BidActions/QuickBidButtons'
import { useExecutedContractIfContractListing } from '../../Bid/BidActions/useExecutedContractIfContractListing'
import {
  ActionButton,
  Bold,
  ActionsBody,
  ChargeDisclaimer,
  SectionContent,
  SectionHeader,
  SectionInfoIcon,
} from '../ui'
import { AuctionBidInput } from './AuctionBidInput'

const ns = 'Auction.details.bidActions'

type BidActionsProps = {
  isPaused: boolean
  editDeliveryMethod: () => void
  auctionDetailState: AuctionDetailState
  userBidAmount: number
  listing: OrchestratedAuctionListingDetails
  order?: OrderResponseDto | null
  shippingQuotePending?: boolean
}

export const BidActions = ({
  isPaused,
  editDeliveryMethod,
  auctionDetailState,
  userBidAmount,
  listing,
  order,
  shippingQuotePending = false,
}: BidActionsProps) => {
  const { auction, contract } = listing
  const mobileScreen = useIsMobile()
  const isLoggedIn = useIsLoggedIn()
  const router = useCompatibleRouter()
  const { trackButtonClicked } = useAnalytics()
  const accountId = useUserAccountId(false)

  const isContract = isContractListing(listing)
  const isSellerCollects = listing.sellerCollects === true
  const bidBasis = contract?.bidBasis ?? 'ABSOLUTE'
  const contractUnit = contract?.bidUnit
  const isRelativeBid = bidBasis === 'RELATIVE'

  const userMinBidAmount = getUserMinBidAmountForAuction(auction)

  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0
  const enableSellingPriceCalculation = useFlag(
    FeatureFlags.ENABLE_BID_BOX_SELLING_PRICE_CALCULATION
  )
  const enableSellingPriceCalculationCostco = useFlag(
    FeatureFlags.ENABLE_BID_BOX_SELLING_PRICE_CALCULATION_COSTCO
  )
  const typicalSellingPriceAnalytics = getTypicalSellingPriceAnalyticsData(
    listing,
    enableSellingPriceCalculation,
    enableSellingPriceCalculationCostco
  )
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)
  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)

  const { values: formikValues, setFieldValue } =
    useFormikContext<BidFormFormikValues>()

  const { auctionState, bidState, qualifiedToBid, eligibleToBid } =
    auctionDetailState

  const listingType = listing.listingType ?? 'SPOT'

  const isUnsupportedListingType = !['SPOT', 'CONTRACT'].includes(listingType)
  useEffect(() => {
    if (isUnsupportedListingType) {
      ddLogger.error('BidActions: unsupported listingType', {
        listingId: listing._id,
        listingType,
      })
    }
  }, []) // oxlint-disable-line eslint-plugin-react-hooks/exhaustive-deps

  const { actualEndTime, bidPrecision, numberOfBids } = auction
  const timeDiffInSeconds = differenceInSeconds(
    // oxlint-disable-next-line react/purity
    parseISO(actualEndTime || new Date().toDateString()),
    // oxlint-disable-next-line react/purity
    new Date()
  )

  const { contract: executedContract } = useExecutedContractIfContractListing({
    accountId,
    bidState,
    isContract,
    listing,
  })

  const { details, selectedQuote } = useBidFormContext()
  const { currency } = details
  const costContext = useCostContextAnalyticsProperties({
    listing,
    selectedQuote,
  })

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: isRelativeBid ? 'percent' : 'currency',
    precision: bidPrecision,
    precisionStored: 1,
  })

  const currentBidString = formatPrecisionNumber(userBidAmount)

  // — WON —
  if (bidState === BidStatus.WON) {
    const showOrderButton = !(isContract && !executedContract?.groupId)
    return showOrderButton ? (
      <ActionsBody>
        <ActionButton
          type="button"
          appearance={order || isContract ? 'primary' : 'loading'}
          size={mobileScreen ? 'medium' : 'large'}
          disabled={!order && !isContract}
          onClick={() => {
            if (!accountId) return
            if (order) {
              router.push(
                `${buyUrlPrefix}${interpUri`/user/${accountId}/orders/details/${order._id}`}`
              )
            }
            if (isContract && executedContract?.groupId) {
              router.push(
                `${buyUrlPrefix}${interpUri`/user/${accountId}/contracts/details/${executedContract.groupId}`}`
              )
            }
          }}
        >
          <FormattedMessage
            id={`${ns}.${isContract ? 'checkContractDetails' : 'checkOrderDetails'}`}
          />
        </ActionButton>
      </ActionsBody>
    ) : null
  }

  // — NOT ACTIVE —
  if (auctionState !== AuctionState.Active) return null

  // — NOT LOGGED IN —
  if (!isLoggedIn) {
    return (
      <ActionsBody>
        <ActionButton
          type="button"
          appearance="primary"
          size={mobileScreen ? 'medium' : 'large'}
          onClick={() => {
            // Track before redirect
            trackButtonClicked({
              buttonName: 'sign_in_to_bid',
              time_left: timeDiffInSeconds,
              bids_existing: numberOfBids,
              ...getListingAnalyticsData(listing, fmvBadgeFlag),
              ...typicalSellingPriceAnalytics,
            })
            redirectToAuth()
          }}
        >
          <FormattedMessage id={`${ns}.signIn`} />
        </ActionButton>
      </ActionsBody>
    )
  }

  // — NOT ELIGIBLE —
  if (!eligibleToBid) return null

  // — NOT QUALIFIED (need to select shipment) —
  if (!qualifiedToBid && !shippingQuotePending) {
    return (
      <ActionsBody>
        <ActionButton
          type="button"
          appearance="primary"
          size={mobileScreen ? 'medium' : 'large'}
          onClick={() => {
            editDeliveryMethod()
            trackButtonClicked({
              buttonName: 'select_shipment_to_bid',
              time_left: timeDiffInSeconds,
              bids_existing: numberOfBids,
              ...getListingAnalyticsData(listing, fmvBadgeFlag),
              ...typicalSellingPriceAnalytics,
            })
          }}
        >
          <FormattedMessage id={`${ns}.selectHowToReceive`} />
        </ActionButton>
      </ActionsBody>
    )
  }

  // — NO BID / LOSING / WINNING: full bid UI —
  const isNoBid = bidState === BidStatus.NO_BID
  const isLosing = bidState === BidStatus.LOSING
  const isWinning = bidState === BidStatus.WINNING

  if (!isNoBid && !isLosing && !isWinning) return null

  const sectionHeader = isNoBid ? (
    <FormattedMessage id={`${ns}.secondary.header.place.${listingType}`} />
  ) : (
    <FormattedMessage id={`${ns}.secondary.header.adjust.${listingType}`} />
  )

  const sectionContent = isLosing ? (
    <FormattedMessage
      id={`${ns}.secondary.content.currentLosingMax`}
      values={{ max: currentBidString }}
    />
  ) : isWinning ? (
    <FormattedMessage
      id={`${ns}.secondary.content.current`}
      values={{ max: currentBidString }}
    />
  ) : undefined

  const primaryAppearance = 'primary'
  const primaryLabel = (
    <>
      <FontAwesomeIcon icon={faGavel} />{' '}
      <FormattedMessage
        id={
          isNoBid
            ? 'Listing.actionColumn.actionCard.bid.bidActions.placeBid'
            : `${ns}.adjustMaxBid.${listingType}`
        }
      />
    </>
  )

  return (
    <ActionsBody>
      <SectionHeader>
        {sectionHeader}
        {isNoBid && (
          <SectionInfoIcon
            size="small"
            tooltipProps={{ placement: 'top', hoverable: true }}
          >
            <FormattedMessage
              id={`${ns}.tooltips.maxBid`}
              values={{
                bold: (chunks: ReactNode) => <Bold>{chunks}</Bold>,
              }}
            />
          </SectionInfoIcon>
        )}
      </SectionHeader>
      {sectionContent && <SectionContent>{sectionContent}</SectionContent>}

      <QuickBidButtons
        listing={listing}
        bidBasis={bidBasis}
        bidPrecision={bidPrecision}
        currency={currency}
        disabled={isPaused}
        userMinBidAmount={userMinBidAmount}
      />

      {!isSellerCollects && (
        <ChargeDisclaimer>
          <FormattedMessage id={`${ns}.autoPayMessage`} />
        </ChargeDisclaimer>
      )}

      <AuctionBidInput
        analyticsData={{ listing, screenName: 'listing_detail_page' }}
        bidBasis={bidBasis}
        bidPrecision={bidPrecision}
        bidUnit={contractUnit}
        currency={currency}
        disabled={isPaused}
        isContract={isContract}
        minBidAmount={userMinBidAmount}
        shortUnit
      />

      <ActionButton
        type="submit"
        appearance={isPaused ? 'loading' : primaryAppearance}
        size={mobileScreen ? 'medium' : 'large'}
        disabled={isPaused}
        onClick={() => {
          if (isNoBid) {
            void setFieldValue('bidType', 'bid_now')
            trackButtonClicked({
              buttonName: 'bid_now',
              bid_amt_usd: formikValues.amount,
              time_left: timeDiffInSeconds,
              bids_existing: numberOfBids,
              bid_type: 'bid_now',
              is_new_bid_box_design: true,
              is_total_landed_cost_displayed: isTlcDisplayed,
              ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
              ...typicalSellingPriceAnalytics,
              ...problemFreeOrderRateAnalytics,
              ...costContext.properties,
            })
          } else {
            trackButtonClicked({
              buttonName: 'adjust_max_bid',
              bid_amt_usd: formikValues.amount,
              time_left: timeDiffInSeconds,
              bids_existing: numberOfBids,
              winning_state: isWinning ? 'winning' : 'losing',
              is_new_bid_box_design: true,
              is_total_landed_cost_displayed: isTlcDisplayed,
              ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
              ...typicalSellingPriceAnalytics,
              ...problemFreeOrderRateAnalytics,
              ...costContext.properties,
            })
          }
        }}
      >
        {primaryLabel}
      </ActionButton>
    </ActionsBody>
  )
}
